import {
  call,
  delay,
  fork,
  put,
  race,
  select,
  take,
  takeEvery,
  takeLatest,
} from "redux-saga/effects";

import Announcement from "@mapmycustomers/shared/types/announcements/Announcement";
import { SimpleListResponse } from "@mapmycustomers/shared/types/viewModel/ListResponse";

import announcementsSettings from "@app/util/announcements/announcementsSettings";
import loggingService from "@app/util/logging";

import { callApi } from "../api/callApi";
import { login, logout } from "../auth/actions";
import authService from "../auth/AuthService";
import { handleError } from "../errors/actions";

import {
  endStartupWait,
  fetchAnnouncements,
  markAnnouncementViewed,
  openAnnouncements,
  resetAnnouncements,
  showNextAnnouncement,
  skipAnnouncements,
} from "./actions";
import {
  clearReportedBrokenIds,
  isAnnouncementsHiddenForSession,
  isAnnouncementsModalOpen,
} from "./selectors";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
const FETCH_LIMIT = 1000;
const STARTUP_WAIT_MS = 3000;

function* fetchAnnouncementsList() {
  try {
    // Newest updates first, so the limit keeps the freshest rows.
    const response: SimpleListResponse<Announcement> = yield callApi("fetchAnnouncements", {
      $limit: FETCH_LIMIT,
      $order: "-updatedAt",
    });
    yield put(fetchAnnouncements.success(response.data));
  } catch (error) {
    // No notification: a failed fetch must surface nothing to the user.
    loggingService.error("Failed to fetch announcements", { error });
    yield put(fetchAnnouncements.failure());
    yield put(handleError({ displayNotification: false, error }));
  }
}

// A sign-out cancels a fetch in flight, so its response cannot reach the next user's session.
export function* onFetchAnnouncements() {
  yield race({ fetch: call(fetchAnnouncementsList), reset: take(resetAnnouncements) });
}

export function* onMarkAnnouncementViewed({
  payload,
}: ReturnType<typeof markAnnouncementViewed.request>) {
  try {
    yield callApi("markAnnouncementViewed", payload);
    yield put(markAnnouncementViewed.success(payload));
  } catch (error) {
    // Not retried and nothing shown; the announcement may simply appear again later.
    loggingService.error("Failed to mark announcement viewed", { announcementId: payload, error });
    yield put(markAnnouncementViewed.failure());
    yield put(handleError({ displayNotification: false, error }));
  }
}

export function* onShowNextAnnouncement({ payload }: ReturnType<typeof showNextAnnouncement>) {
  yield put(markAnnouncementViewed.request(payload));
}

export function* onLogout() {
  yield call(clearReportedBrokenIds);
  yield put(resetAnnouncements());
}

// Signing out ends a CTA tab's announcement-free session, so the next user gets announcements.
export function* onSignOut() {
  yield call(announcementsSettings.setAnnouncementsOffInTab, false);
}

function* waitForModalToClose() {
  let open: boolean = yield select(isAnnouncementsModalOpen);
  while (open) {
    yield take("*");
    open = yield select(isAnnouncementsModalOpen);
  }
}

// Delay first so the poll never races the first fetch; the 5-minute wait restarts when the modal closes.
export function* pollAnnouncements() {
  while (true) {
    const { opened }: { opened?: ReturnType<typeof openAnnouncements> } = yield race({
      elapsed: delay(POLL_INTERVAL_MS),
      opened: take(openAnnouncements),
    });
    if (opened) {
      yield call(waitForModalToClose);
      continue;
    }
    const hiddenForSession: boolean = yield select(isAnnouncementsHiddenForSession);
    if (hiddenForSession || (typeof document !== "undefined" && document.hidden)) {
      continue;
    }
    if (authService.hasToken()) {
      yield put(fetchAnnouncements.request());
    }
  }
}

function* waitForFirstGateDecision() {
  const { failure }: { failure?: ReturnType<typeof fetchAnnouncements.failure> } = yield race({
    failure: take(fetchAnnouncements.failure),
    success: take(fetchAnnouncements.success),
  });
  if (!failure) {
    yield take([openAnnouncements, skipAnnouncements]);
  }
}

// Auto-running guides wait for the gate's first decision after the first fetch, at most STARTUP_WAIT_MS.
export function* endStartupWaitAfterFirstFetch() {
  while (true) {
    yield take(fetchAnnouncements.request);
    const { reset }: { reset?: ReturnType<typeof resetAnnouncements> } = yield race({
      reset: take(resetAnnouncements),
      settled: call(waitForFirstGateDecision),
      timedOut: delay(STARTUP_WAIT_MS),
    });
    if (reset) {
      continue;
    }
    yield put(endStartupWait());
    yield take(resetAnnouncements);
  }
}

export function* announcementsSaga() {
  yield takeLatest(fetchAnnouncements.request, onFetchAnnouncements);
  yield takeEvery(showNextAnnouncement, onShowNextAnnouncement);
  yield takeEvery(markAnnouncementViewed.request, onMarkAnnouncementViewed);
  yield takeEvery([logout, login], onLogout);
  yield takeEvery(logout, onSignOut);
  yield fork(pollAnnouncements);
  yield fork(endStartupWaitAfterFirstFetch);
}
